import React, {
  Suspense,
  useEffect,
  useLayoutEffect,
  useState,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { createPortal } from "react-dom";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Canvas, useThree, useFrame } from "@react-three/fiber";
import { OrbitControls, Html } from "@react-three/drei";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import { DRACOLoader } from "three/examples/jsm/loaders/DRACOLoader";
import { convertToSignedUrl } from "../../utils/wasabiHelper";
import Fireworks from "./Fireworks";
import * as THREE from "three";

import introJs from "intro.js";
import "intro.js/introjs.css";
import {
  Button,
  Typography,
  Card,
  Spin,
  Alert,
  Tooltip,
  Collapse,
  App,
  theme,
} from "antd";
import {
  ArrowLeft,
  ArrowRight,
  CircleHelp,
  CircleCheck,
  Home,
  RotateCcw,
  CirclePlay,
  Book,
  FileText,
} from "lucide-react";
import Icon from "../../components/Icon";

import MainLayout from "../../components/MainLayout";
import ContentGuide from "../../components/ContentGuide";
import YouTubePlayer from "./components/YouTubePlayer";
import VimeoPlayer from "./components/VimeoPlayer";
import { useAuth } from "../../auth/auth.context";
import { useUserProgress } from "../../hooks/useUserProgress";
import {
  getModule,
  getModuleSteps,
  getNextUncompletedModule,
  getModuleQuiz,
  isLastModuleInCourse,
  getNextModuleInCourse,
  findCourseByModuleId,
} from "../../services/moduleService";
import {
  updateModuleProgress,
  getModuleProgress,
  completeCourse,
} from "../../services/userProgressService";
import Hotspot from "./components/Hotspot";
import { useModuleQuiz } from "../../hooks/useModuleQuiz";
import { useIsMobile } from "../../hooks/useBreakpoint";
import { useThemeMode } from "../../theme/ThemeProvider";
import { alpha } from "../../utils/color";
import RichContentViewer from "../../components/RichContentViewer";
import { isRichContentEmpty } from "../../utils/richContent";
import { getSafeImageUrl } from "../../utils/imageHelper";

// Custom-page steps render the whole experience inside the rich-content
// viewer (LessonDetails → RichContentViewer with inline 3D / video / image
// embeds), so we skip the legacy 16/9 step-type viewer at the top.
const isCustomPageStep = (step) => step?.stepType === "custom-page";

function ModelLoadingIndicator() {
  return (
    <Html center zIndexRange={[10, 0]}>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <style>
          {`
            .airsmart-model-spin,
            .airsmart-model-spin .ant-spin-dot-holder {
              color: #111827 !important;
            }
            .airsmart-model-spin .ant-spin-dot-item {
              background-color: #111827 !important;
            }
          `}
        </style>
        <Spin className="airsmart-model-spin" />
      </div>
    </Html>
  );
}

// 3D Model Component - GLTF/GLB Only (similar to StepEditor)
function Model({ modelUrl, onModelClick, onLoadStart, onLoadComplete, layers }) {
  const meshRef = useRef();
  const [loadedModel, setLoadedModel] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (modelUrl) {
      setLoading(true);
      setError(null);
      setLoadedModel(null);
      onLoadStart?.();

      const gltfLoader = new GLTFLoader();

      // Setup DRACO loader for compressed models
      const dracoLoader = new DRACOLoader();
      dracoLoader.setDecoderPath(
        "https://www.gstatic.com/draco/versioned/decoders/1.5.6/"
      );
      gltfLoader.setDRACOLoader(dracoLoader);

      gltfLoader.load(
        modelUrl,
        // onLoad
        (gltf) => {
          // Auto-scale to fit view (Synced with ThreeDModelTemplateEditor)
          const box = new THREE.Box3().setFromObject(gltf.scene);
          const size = box.getSize(new THREE.Vector3()).length();
          const scale = size > 0 ? 5 / size : 1;
          gltf.scene.scale.setScalar(scale);

          // Center the model
          const center = box.getCenter(new THREE.Vector3());
          gltf.scene.position.sub(center.multiplyScalar(scale));

          setLoadedModel(gltf);
          setLoading(false);

          // Notify parent that model has loaded
          if (onLoadComplete) {
            onLoadComplete(true);
          }
        },
        undefined,
        // onError
        (error) => {
          console.error("Error loading GLTF model:", error);
          setError(error);
          setLoading(false);

          // Notify parent that model failed to load
          if (onLoadComplete) {
            onLoadComplete(false);
          }
        }
      );
    } else {
      setLoadedModel(null);
      setLoading(false);
      setError(null);
    }
  }, [modelUrl, onLoadStart, onLoadComplete]);

  // Update mesh visibility when layers change
  useEffect(() => {
    if (!loadedModel || !layers) return;
    loadedModel.scene.traverse((child) => {
      if (child.isMesh) {
        const saved = layers.find(
          (l) => l.name === child.name || l.uuid === child.uuid
        );
        if (saved !== undefined) {
          child.visible = saved.visible !== false;
        }
      }
    });
  }, [loadedModel, layers]);

  useFrame(() => {
    if (meshRef.current) {
      // Any animations can go here
    }
  });

  // Show loading state
  if (loading) {
    return <ModelLoadingIndicator />;
  }

  // Show error state
  if (error) {
    return (
      <Html center zIndexRange={-1}>
        <div
          style={{
            color: "#d32f2f", // AirSmart error color
            padding: "24px",
            fontSize: "14px",
            textAlign: "center",
            minWidth: "250px",
            backdropFilter: "blur(4px)",
          }}
        >
          <div
            style={{
              fontWeight: 600,
              marginBottom: "8px",
              fontSize: "16px",
            }}
          >
            Failed to load 3D model
          </div>
          <div
            style={{
              fontSize: "13px",
              color: "gray",
              lineHeight: "1.4",
            }}
          >
            Please check the model URL and CORS settings
          </div>
        </div>
      </Html>
    );
  }

  // Only render if we have a valid model
  if (!loadedModel) {
    return null;
  }

  return (
    <primitive
      ref={meshRef}
      object={loadedModel.scene}
      onClick={onModelClick}
    />
  );
}

// Fallback component for when no model is loaded
function DefaultScene({ onLoadComplete }) {
  useEffect(() => {
    onLoadComplete?.(true);
  }, [onLoadComplete]);

  return (
    <mesh>
      <boxGeometry args={[2, 2, 2]} />
      <meshStandardMaterial color="#cccccc" />
    </mesh>
  );
}

// Updated ModelWithMemo component to use GLTF/GLB only
const ModelWithMemo = React.memo(function ModelWithMemo({
  modelUrl,
  onLoadStart,
  onLoadComplete,
  layers,
}) {
  // Check if URL is from Wasabi storage
  const isWasabiUrl = (url) => {
    if (!url) return false;
    return (
      url.includes("wasabisys.com") ||
      url.includes("airsmart") ||
      !url.includes("://")
    ); // If no protocol, assume it's a Wasabi path
  };

  // Process URL - create signed URL if it's a Wasabi URL
  let finalUrl = modelUrl;
  if (isWasabiUrl(finalUrl)) {
    finalUrl = convertToSignedUrl(finalUrl);
  }
  return (
    <Suspense fallback={null}>
      {finalUrl ? (
        <Model
          modelUrl={finalUrl}
          onLoadStart={onLoadStart}
          onLoadComplete={onLoadComplete}
          layers={layers}
        />
      ) : (
        <DefaultScene onLoadComplete={onLoadComplete} />
      )}
    </Suspense>
  );
});

// Camera controller component to handle step-specific camera settings
const CameraController = ({ cameraSettings, controlsRef }) => {
  const { camera } = useThree();

  // Effect to sync camera position when cameraSettings prop changes - matching admin_panel approach
  useLayoutEffect(() => {
    if (!cameraSettings) return;

    if (cameraSettings.position) {
      camera.position.set(
        cameraSettings.position[0],
        cameraSettings.position[1],
        cameraSettings.position[2]
      );
    }
    if (cameraSettings.fov !== undefined) camera.fov = cameraSettings.fov;
    if (cameraSettings.zoom !== undefined) camera.zoom = cameraSettings.zoom;

    if (controlsRef.current && cameraSettings.target) {
      controlsRef.current.target.set(
        cameraSettings.target[0],
        cameraSettings.target[1],
        cameraSettings.target[2]
      );
      controlsRef.current.update();
    }

    camera.updateProjectionMatrix();
  }, [camera, cameraSettings, controlsRef]);

  return null;
};

// Reset camera function
const resetCameraToDefault = (camera, controlsRef, currentStep) => {
  if (!controlsRef.current || !camera) {
    console.warn("Controls or camera not available");
    return false;
  }

  // Get default position and target from current step or use fallback
  const defaultPosition = currentStep?.camera?.position || [5, 5, 5];
  const defaultTarget = currentStep?.camera?.target || [0, 0, 0];
  
  // Set camera position
  camera.position.set(
    defaultPosition[0],
    defaultPosition[1],
    defaultPosition[2]
  );

  // Set camera target
  controlsRef.current.target.set(
    defaultTarget[0],
    defaultTarget[1],
    defaultTarget[2]
  );

  // Update controls to apply changes
  controlsRef.current.update();
  return true;
};

// Hotspots component - using new Hotspot component with tooltip
const HotspotsRenderer = React.memo(function HotspotsRenderer({
  hotspots = [],
  onHotspotClick,
}) {
  return (
    <>
      {hotspots &&
        hotspots.length > 0 &&
        hotspots.map((hotspot, index) => (
          <Hotspot
            key={hotspot.id || index}
            hotspot={hotspot}
            index={index}
            onHotspotClick={onHotspotClick}
          />
        ))}
    </>
  );
});

// Helper: keep legacy enum colors working while preferring StepEditor-style hex colors.
const getAnnotationBgColor = (colorName) => {
  if (typeof colorName === "string" && colorName.startsWith("#")) return colorName;
  switch (colorName) {
    case "primary": return "#2196f3";
    case "secondary": return "#9c27b0";
    case "error": return "#f44336";
    case "warning": return "#ff9800";
    case "success": return "#4caf50";
    default: return "#1976d2";
  }
};

const getAnnotationColor = (annotation) =>
  annotation?.color || getAnnotationBgColor(annotation?.style?.color);

const getAnnotationTooltipStyle = (annotation, isMobile) => {
  const x = Number(annotation?.position?.x ?? 50);
  const y = Number(annotation?.position?.y ?? 50);
  const nearLeft = x <= 35;
  const nearRight = x >= 65;
  const nearBottom = y >= 58;

  const horizontalPlacement = nearLeft
    ? { left: 0, transform: "translateX(0)" }
    : nearRight
      ? { left: "100%", transform: "translateX(-100%)" }
      : { left: "50%", transform: "translateX(-50%)" };

  const verticalPlacement = nearBottom
    ? { bottom: "100%", marginBottom: isMobile ? 6 : 12 }
    : { top: "100%", marginTop: isMobile ? 6 : 12 };

  return {
    position: "absolute",
    ...verticalPlacement,
    ...horizontalPlacement,
    padding: isMobile ? 8 : 16,
    minWidth: isMobile ? 160 : 200,
    maxWidth: isMobile ? "min(220px, calc(100vw - 40px))" : 280,
    width: isMobile ? "max-content" : "auto",
    maxHeight: isMobile ? 140 : "none",
    overflowY: isMobile ? "auto" : "visible",
    zIndex: 50,
    background: "var(--ant-color-bg-container, #fff)",
    color: "var(--ant-color-text, #000)",
    borderRadius: 8,
    boxShadow: "0 4px 20px rgba(0,0,0,0.2)",
  };
};

// Enhanced Image Viewer Component with Annotations
const ImageViewerWithAnnotations = React.memo(
  function ImageViewerWithAnnotations({ step }) {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedAnnotation, setSelectedAnnotation] = useState(null);
    const imageFrameRef = useRef(null);
    const isMobile = useIsMobile();

    const handleLoad = () => {
      setLoading(false);
    };

    const handleError = () => {
      setLoading(false);
      setError("Failed to load image");
    };

    // Check if URL is from Wasabi storage
    const isWasabiUrl = (url) => {
      if (!url) return false;
      return (
        url.includes("wasabisys.com") ||
        url.includes("airsmart") ||
        !url.includes("://")
      );
    };

    // Process image URL - convert to signed URL if from Wasabi
    const processImageUrl = (imageUrl) => {
      if (!imageUrl) return null;

      if (isWasabiUrl(imageUrl)) {
        const signedUrl = convertToSignedUrl(imageUrl);
        return signedUrl;
      }

      return imageUrl;
    };

    const finalImageUrl = processImageUrl(step?.imageUrl);
    const imageAnnotations = step?.imageAnnotations || [];

    const getMobileAnnotationTooltipStyle = (annotation) => {
      if (typeof window === "undefined") {
        return {};
      }

      const rect = imageFrameRef.current?.getBoundingClientRect();
      const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 320;
      const scrollX = window.scrollX || window.pageXOffset || 0;
      const scrollY = window.scrollY || window.pageYOffset || 0;
      const tooltipWidth = Math.min(280, Math.max(180, viewportWidth - 32));
      const markerX = rect
        ? rect.left + rect.width * ((annotation.position?.x ?? 50) / 100)
        : viewportWidth / 2;
      const markerY = rect
        ? rect.top + rect.height * ((annotation.position?.y ?? 50) / 100)
        : 0;
      const left = Math.min(
        Math.max(markerX + scrollX, scrollX + 16 + tooltipWidth / 2),
        scrollX + viewportWidth - 16 - tooltipWidth / 2
      );

      return {
        position: "absolute",
        top: markerY + scrollY + 18,
        left,
        transform: "translateX(-50%)",
        width: tooltipWidth,
        zIndex: 10000,
      };
    };

    const renderAnnotationTooltip = (annotation, index) => {
      const tooltip = (
        <div
          style={{
            position: "absolute",
            top: isMobile ? undefined : "100%",
            left: isMobile ? undefined : "50%",
            transform: isMobile ? undefined : "translateX(-50%)",
            marginTop: isMobile ? 0 : 12,
            padding: isMobile ? 8 : 16,
            minWidth: isMobile ? undefined : "200px",
            maxWidth: isMobile ? undefined : "280px",
            width: isMobile ? undefined : "auto",
            zIndex: isMobile ? 10000 : 20,
            background: "var(--ant-color-bg-container, #fff)",
            color: "var(--ant-color-text, #000)",
            borderRadius: 8,
            boxShadow: "0 4px 20px rgba(0,0,0,0.2)",
            ...(isMobile ? getMobileAnnotationTooltipStyle(annotation) : {}),
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              marginBottom: isMobile ? 4 : 8,
            }}
          >
            <Typography.Text
              strong
              style={{
                color: "#1677ff",
                fontSize: isMobile ? "11px" : "0.875rem",
                wordWrap: "break-word",
                overflowWrap: "break-word",
                wordBreak: "break-word",
              }}
            >
              {annotation.title || annotation.label || `Annotation ${index + 1}`}
            </Typography.Text>
            <Button
              type="text"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedAnnotation(null);
              }}
              style={{ padding: 4, marginLeft: 8, marginTop: -4, marginRight: -4 }}
            >
              &#10005;
            </Button>
          </div>
          {annotation.description && (
            <Typography.Text
              type="secondary"
              style={{
                display: "block",
                fontSize: isMobile ? "10px" : "0.875rem",
                wordWrap: "break-word",
                overflowWrap: "break-word",
                wordBreak: "break-word",
              }}
            >
              {annotation.description}
            </Typography.Text>
          )}
        </div>
      );

      if (isMobile && typeof document !== "undefined") {
        return createPortal(
          tooltip,
          document.body,
          `image-annotation-tooltip-${annotation.id || index}`
        );
      }

      return tooltip;
    };

    return (
      <div
        style={{
          width: "100%",
          height: "100%",
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
        }}
      >
        {loading && (
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              zIndex: 10,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Spin />
            <Typography.Text style={{ marginTop: 8, textAlign: "center" }}>
              Loading image...
            </Typography.Text>
          </div>
        )}

        {error && (
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              zIndex: 10,
            }}
          >
            <Alert type="error" message={error} />
          </div>
        )}

        {/* Image with Annotations Wrapper */}
        <div
          ref={imageFrameRef}
          style={{
            position: "relative",
            width: "auto",
            height: "auto",
            maxWidth: "100%",
            maxHeight: "100%",
            display: "inline-flex", // Use inline-flex to shrink-wrap the image exactly
          }}
        >
          <img
            src={finalImageUrl}
            alt={step.title}
            style={{
              maxWidth: "100%",
              maxHeight: "100%",
              width: "auto",
              height: "auto",
              objectFit: "contain",
              display: loading ? "none" : "block", // Ensure no whitespace below image
            }}
            onLoad={handleLoad}
            onError={handleError}
          />

          {/* Image Annotations Overlay */}
          {!loading &&
            imageAnnotations.length > 0 &&
            imageAnnotations.map((annotation, index) => (
              <div
                key={annotation.id || index}
                style={{
                  position: "absolute",
                  left: `${annotation.position?.x || 50}%`,
                  top: `${annotation.position?.y || 50}%`,
                  transform: "translate(-50%, -50%)",
                  zIndex: selectedAnnotation?.id === annotation.id ? 20 : 10,
                  pointerEvents: "auto",
                }}
              >
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedAnnotation(
                      selectedAnnotation?.id === annotation.id ? null : annotation
                    );
                  }}
                  style={{
                    minWidth: "auto",
                    borderRadius: "50%",
                    width: 24,
                    height: 24,
                    fontSize: "10px",
                    fontWeight: "bold",
                    backgroundColor: getAnnotationColor(annotation),
                    color: "#fff",
                    border: "2px solid white",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transition: "transform 0.2s",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.5)",
                    padding: 0, // Reset padding
                    margin: 0,  // Reset margin
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "scale(1.1)";
                    e.currentTarget.style.backgroundColor = getAnnotationColor(annotation);
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "scale(1)";
                    e.currentTarget.style.backgroundColor = getAnnotationColor(annotation);
                  }}
                >
                  {index + 1}
                </button>

                {/* Mobile tooltips are portaled outside the image frame so overflow cannot clip them. */}
                {selectedAnnotation?.id === annotation.id &&
                  renderAnnotationTooltip(annotation, index)}
              </div>
            ))}
        </div>
      </div>
    );
  }
);

// Enhanced Video Viewer Component with Annotations
const VideoViewerWithAnnotations = React.memo(
  function VideoViewerWithAnnotations({ step }) {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentTime, setCurrentTime] = useState(0);
    const [selectedAnnotation, setSelectedAnnotation] = useState(null);
    const [annotationPosition, setAnnotationPosition] = useState({
      x: 0,
      y: 0,
    });
    const videoRef = useRef(null);
    const videoFrameRef = useRef(null);
    const isMobile = useIsMobile();

    // Memoize URLs to prevent recreation on every render
    const finalVideoUrl = useMemo(() => {
      const processVideoUrl = (videoUrl) => {
        if (!videoUrl) return null;

        const isWasabiUrl = (url) => {
          if (!url) return false;
          return (
            url.includes("wasabisys.com") ||
            url.includes("airsmart") ||
            !url.includes("://")
          );
        };

        if (isWasabiUrl(videoUrl)) {
          const signedUrl = convertToSignedUrl(videoUrl);
          return signedUrl;
        }

        return videoUrl;
      };

      return processVideoUrl(step?.videoUrl);
    }, [step?.videoUrl]);

    // Memoize video type detection and extract video IDs
    const { isEmbed, embedUrl, videoType, videoId } = useMemo(() => {
      const getVideoInfo = (url) => {
        if (!url)
          return {
            isEmbed: false,
            embedUrl: null,
            videoType: null,
            videoId: null,
          };

        // YouTube URLs
        if (url.includes("youtube.com/watch?v=")) {
          const id = url.split("v=")[1]?.split("&")[0];
          return {
            isEmbed: true,
            embedUrl: `https://www.youtube.com/embed/${id}`,
            videoType: "youtube",
            videoId: id,
          };
        }
        if (url.includes("youtu.be/")) {
          const id = url.split("youtu.be/")[1]?.split("?")[0];
          return {
            isEmbed: true,
            embedUrl: `https://www.youtube.com/embed/${id}`,
            videoType: "youtube",
            videoId: id,
          };
        }
        if (url.includes("youtube.com/embed/")) {
          const id = url.split("embed/")[1]?.split("?")[0];
          return {
            isEmbed: true,
            embedUrl: url,
            videoType: "youtube",
            videoId: id,
          };
        }

        // Vimeo URLs
        if (url.includes("vimeo.com/") && !url.includes("player.vimeo.com")) {
          const id = url.split("vimeo.com/")[1]?.split("?")[0];
          return {
            isEmbed: true,
            embedUrl: `https://player.vimeo.com/video/${id}`,
            videoType: "vimeo",
            videoId: id,
          };
        }
        if (url.includes("player.vimeo.com/video/")) {
          const id = url.split("video/")[1]?.split("?")[0];
          return {
            isEmbed: true,
            embedUrl: url,
            videoType: "vimeo",
            videoId: id,
          };
        }

        return {
          isEmbed: false,
          embedUrl: null,
          videoType: null,
          videoId: null,
        };
      };

      return getVideoInfo(finalVideoUrl);
    }, [finalVideoUrl]);

    // Memoize video annotations to prevent array recreation
    const videoAnnotations = useMemo(
      () => step?.videoAnnotations || [],
      [step?.videoAnnotations]
    );

    // Memoize event handlers to prevent recreation
    const handleLoad = useCallback(() => {
      setLoading(false);
    }, []);

    const handleError = useCallback(() => {
      setLoading(false);
      setError("Failed to load video");
    }, []);

    // Handle time update for video annotations
    const handleTimeUpdate = useCallback(() => {
      if (videoRef.current) {
        setCurrentTime(videoRef.current.currentTime);
      }
    }, []);

    // Handle time update for embed players (YouTube, Vimeo)
    const handleEmbedTimeUpdate = useCallback((time) => {
      setCurrentTime(time);
    }, []);

    // Handle embed player ready
    const handleEmbedReady = useCallback(() => {
      setLoading(false);
    }, []);

    // Handle embed player error
    const handleEmbedError = useCallback((error) => {
      setLoading(false);
      setError("Failed to load video");
      console.error("Embed player error:", error);
    }, []);

    // Only setup event listeners when video ref changes and it's not embed
    useEffect(() => {
      const video = videoRef.current;
      if (video && !isEmbed) {
        video.addEventListener("timeupdate", handleTimeUpdate);
        return () => {
          video.removeEventListener("timeupdate", handleTimeUpdate);
        };
      }
    }, [isEmbed, handleTimeUpdate]);

    // Reset loading state when video URL changes
    useEffect(() => {
      if (finalVideoUrl) {
        setLoading(true);
        setError(null);
      }
    }, [finalVideoUrl]);

    const getMobileAnnotationTooltipStyle = (annotation) => {
      if (typeof window === "undefined") {
        return {};
      }

      const rect = videoFrameRef.current?.getBoundingClientRect();
      const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 320;
      const scrollX = window.scrollX || window.pageXOffset || 0;
      const scrollY = window.scrollY || window.pageYOffset || 0;
      const tooltipWidth = Math.min(280, Math.max(180, viewportWidth - 32));
      const markerX = rect
        ? rect.left + rect.width * ((annotation.position?.x ?? 50) / 100)
        : viewportWidth / 2;
      const markerY = rect
        ? rect.top + rect.height * ((annotation.position?.y ?? 50) / 100)
        : 0;
      const left = Math.min(
        Math.max(markerX + scrollX, scrollX + 16 + tooltipWidth / 2),
        scrollX + viewportWidth - 16 - tooltipWidth / 2
      );

      return {
        position: "absolute",
        top: markerY + scrollY + 18,
        left,
        transform: "translateX(-50%)",
        width: tooltipWidth,
        zIndex: 10000,
      };
    };

    const renderAnnotationTooltip = (annotation, index) => {
      const tooltip = (
        <div
          style={{
            position: "absolute",
            top: isMobile ? undefined : "100%",
            left: isMobile ? undefined : "50%",
            transform: isMobile ? undefined : "translateX(-50%)",
            marginTop: isMobile ? 0 : 12,
            padding: isMobile ? 8 : 16,
            minWidth: isMobile ? undefined : "200px",
            maxWidth: isMobile ? undefined : "280px",
            width: isMobile ? undefined : "auto",
            zIndex: isMobile ? 10000 : 20,
            background: "var(--ant-color-bg-container, #fff)",
            color: "var(--ant-color-text, #000)",
            borderRadius: 8,
            boxShadow: "0 4px 20px rgba(0,0,0,0.2)",
            ...(isMobile ? getMobileAnnotationTooltipStyle(annotation) : {}),
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              marginBottom: isMobile ? 4 : 8,
            }}
          >
            <Typography.Text
              strong
              style={{
                color: "#1677ff",
                fontSize: isMobile ? "11px" : "0.875rem",
                wordWrap: "break-word",
                overflowWrap: "break-word",
                wordBreak: "break-word",
              }}
            >
              {annotation.title || annotation.label || `Annotation ${index + 1}`}
            </Typography.Text>
            <Button
              type="text"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedAnnotation(null);
              }}
              style={{ padding: 4, marginLeft: 8, marginTop: -4, marginRight: -4 }}
            >
              &#10005;
            </Button>
          </div>
          {annotation.description && (
            <Typography.Text
              type="secondary"
              style={{
                display: "block",
                marginBottom: 8,
                fontSize: isMobile ? "10px" : "0.875rem",
                wordWrap: "break-word",
                overflowWrap: "break-word",
                wordBreak: "break-word",
              }}
            >
              {annotation.description}
            </Typography.Text>
          )}
          {annotation.startTime !== undefined && (
            <Typography.Text
              type="secondary"
              style={{
                display: "block",
                marginTop: annotation.description ? 6 : 0,
                fontSize: "12px",
              }}
            >
              {Math.floor(annotation.startTime)}s -{" "}
              {Math.floor(annotation.endTime)}s
            </Typography.Text>
          )}
        </div>
      );

      if (isMobile && typeof document !== "undefined") {
        return createPortal(
          tooltip,
          document.body,
          `video-annotation-tooltip-${annotation.id || index}`
        );
      }

      return tooltip;
    };

    return (
      <div
        style={{
          width: "100%",
          height: "100%",
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {loading && (
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              zIndex: 10,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Spin />
            <Typography.Text style={{ marginTop: 8, textAlign: "center" }}>
              Loading video...
            </Typography.Text>
          </div>
        )}

        {error && (
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              zIndex: 10,
            }}
          >
            <Alert type="error" message={error} />
          </div>
        )}

        {/* Video Player Wrapper */}
        <div
          ref={videoFrameRef}
          style={{
            position: "relative",
            width: "100%",
            height: "100%",
            maxWidth: "100%",
            maxHeight: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            borderRadius: "8px",
            overflow: "hidden",
          }}
        >
          {isEmbed ? (
            // Render API-enabled players for YouTube and Vimeo with time tracking
            videoType === "youtube" ? (
              <YouTubePlayer
                key={videoId}
                videoId={videoId}
                onTimeUpdate={handleEmbedTimeUpdate}
                onReady={handleEmbedReady}
                onError={handleEmbedError}
                autoPlay={step?.autoPlay}
              />
            ) : videoType === "vimeo" ? (
              <VimeoPlayer
                key={videoId}
                videoId={videoId}
                onTimeUpdate={handleEmbedTimeUpdate}
                onReady={handleEmbedReady}
                onError={handleEmbedError}
                autoPlay={step?.autoPlay}
              />
            ) : (
              // Fallback to iframe for other embed types
              <iframe
                key={embedUrl}
                src={embedUrl}
                width="100%"
                height="100%"
                style={{
                  border: "none",
                  borderRadius: "8px",
                  display: loading ? "none" : "block",
                }}
                onLoad={handleLoad}
                onError={handleError}
                title={`Video for ${step?.title || "Step"}`}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            )
          ) : (
            // Render video element for direct video files
            <video
              key={finalVideoUrl}
              ref={videoRef}
              src={finalVideoUrl}
              controls
              autoPlay={step?.autoPlay || false}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
                borderRadius: "8px",
                display: loading ? "none" : "block",
              }}
              onLoadedData={(e) => {
                handleLoad();
                if (step?.autoPlay && videoRef.current) {
                  try {
                    const p = videoRef.current.play();
                    if (p && typeof p.then === 'function') {
                      p.catch(() => { });
                    }
                  } catch (_) { }
                }
              }}
              onError={handleError}
            />
          )}

          {/* Video Annotations Overlay - Show for all video types with time tracking */}
          {!loading &&
            videoAnnotations.length > 0 &&
            videoAnnotations.map((annotation, index) => {
              // Show annotations based on current time for all video types
              // YouTube and Vimeo now support time tracking via APIs
              const isActive =
                currentTime >= (annotation.startTime || 0) &&
                currentTime <= (annotation.endTime || Infinity);
              if (!isActive) return null;

              return (
                <div
                  key={annotation.id || index}
                  style={{
                    position: "absolute",
                    left: `${annotation.position?.x || 50}%`,
                    top: `${annotation.position?.y || 50}%`,
                    transform: "translate(-50%, -50%)",
                    zIndex: selectedAnnotation?.id === annotation.id ? 20 : 10,
                    pointerEvents: "auto",
                  }}
                >
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedAnnotation(
                        selectedAnnotation?.id === annotation.id ? null : annotation
                      );
                    }}
                    style={{
                      minWidth: "auto",
                      borderRadius: "50%",
                      width: 24,
                      height: 24,
                      fontSize: "10px",
                      fontWeight: "bold",
                      backgroundColor: getAnnotationColor(annotation),
                      color: "#fff",
                      border: "2px solid white",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      transition: "transform 0.2s",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.5)",
                      padding: 0, // Reset padding
                      margin: 0,  // Reset margin
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "scale(1.1)";
                      e.currentTarget.style.backgroundColor = getAnnotationColor(annotation);
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "scale(1)";
                      e.currentTarget.style.backgroundColor = getAnnotationColor(annotation);
                    }}
                  >
                    {index + 1}
                  </button>

                  {/* Mobile tooltips are portaled outside the video frame so iframe/overflow cannot clip them. */}
                  {selectedAnnotation?.id === annotation.id &&
                    renderAnnotationTooltip(annotation, index)}
                </div>
              );
            })}
        </div>
      </div>
    );
  }
);

// Enhanced Media Viewer Component that chooses the right viewer
const MediaViewer = React.memo(function MediaViewer({ step }) {
  const hasVideo = step?.videoUrl;
  const hasImage = step?.imageUrl;

  if (!hasVideo && !hasImage) {
    return null;
  }

  if (hasVideo) {
    return <VideoViewerWithAnnotations step={step} />;
  }

  if (hasImage) {
    return <ImageViewerWithAnnotations step={step} />;
  }

  return null;
});

function ModuleViewer() {
  const { moduleId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Kiểm tra nếu đang trong chế độ học lại (retake mode)
  const isRetakeMode = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("retake") === "true";
  }, [location.search]);

  const { currentUser } = useAuth();
  const {
    isModuleCompleted: checkModuleCompleted,
    getProgressModuleVersion,
    refreshProgress,
  } = useUserProgress();
  const { mode } = useThemeMode();
  const isMobile = useIsMobile();
  const { message } = App.useApp();

  // State variables
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [module, setModule] = useState(null);
  const [course, setCourse] = useState(null);
  const [steps, setSteps] = useState([]);
  const [activeStep, setActiveStep] = useState(0);
  const [moduleCompleted, setModuleCompleted] = useState(false);
  const [nextModule, setNextModule] = useState(null);
  const [hasQuiz, setHasQuiz] = useState(false); // New state to track if module has quiz
  const [quizLoading, setQuizLoading] = useState(false); // Loading state for quiz check
  const [initialLoadComplete, setInitialLoadComplete] = useState(false); // Track initial load
  const [showContentGuide, setShowContentGuide] = useState(false); // Content guide state
  const [completedCourseName, setCompletedCourseName] = useState(null); // Tên course vừa hoàn thành
  const [mainModelLoaded, setMainModelLoaded] = useState(false); // Track main model loading
  const [showFireworks, setShowFireworks] = useState(false); // Track fireworks display
  const [isActionLoading, setIsActionLoading] = useState(false); // Trạng thái chờ API (next/complete)
  const { data: quizData, isLoading: isQuizLoading, error: quizError } = useModuleQuiz(moduleId);
  // Refs to track progress updates
  const prevStepRef = useRef(null);
  // Ref for OrbitControls - matching EditStepEditor approach
  const controlsRef = useRef();
  const mainModelReadyTimerRef = useRef(null);
  const isUpdatingProgressRef = useRef(false); // Prevent multiple simultaneous updates
  const stepsContainerRef = useRef(null); // Ref for auto-scroll to active step
  const { token: antToken } = theme.useToken();
  const isMobileView = useIsMobile(); // Use separate variable to avoid conflict with imported useIsMobile

  // Memoize OrbitControls settings to prevent re-attachments
  const orbitControlsSettings = useMemo(() => ({
    touches: {
      ONE: THREE.TOUCH.ROTATE,
      TWO: THREE.TOUCH.DOLLY_PAN,
    },
    mouseButtons: {
      LEFT: THREE.MOUSE.ROTATE,
      MIDDLE: THREE.MOUSE.DOLLY,
      RIGHT: THREE.MOUSE.PAN,
    }
  }), []);

  // Auto-scroll to active step panel
  useEffect(() => {
    if (stepsContainerRef.current) {
      const activePanel = stepsContainerRef.current.querySelector(`[data-step-index="${activeStep}"]`);
      if (activePanel) {
        activePanel.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }
    }
  }, [activeStep]);



  // Handler for fireworks completion
  const handleFireworksComplete = () => {
    setShowFireworks(false);
  };

  // Handler for hotspot click
  const handleHotspotClick = useCallback((hotspot) => {
    // You can add additional logic here if needed
    // For example, tracking analytics, showing additional info, etc.
  }, []);

  const handleMainModelLoadStart = useCallback(() => {
    if (mainModelReadyTimerRef.current) {
      clearTimeout(mainModelReadyTimerRef.current);
    }
    setMainModelLoaded(false);
  }, []);

  // Handler for main model load complete
  const handleMainModelLoadComplete = useCallback((success) => {
    if (mainModelReadyTimerRef.current) {
      clearTimeout(mainModelReadyTimerRef.current);
    }
    if (success) {
      // Add a small delay to ensure everything (attached models, textures) is settled
      // before showing hotspots to avoid visual artifacts
      mainModelReadyTimerRef.current = setTimeout(() => {
        setMainModelLoaded(true);
      }, 500);
    }
  }, []);

  useEffect(() => {
    return () => {
      if (mainModelReadyTimerRef.current) {
        clearTimeout(mainModelReadyTimerRef.current);
      }
    };
  }, []);

  // Note: Redundant nextModule effect removed to maintain course scope.
  // Scope is correctly handled by the loadNextModuleInfo effect below.

  // Load module and steps data
  useEffect(() => {
    let isMounted = true;

    const loadModuleData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Get module data
        const moduleData = await getModule(moduleId);
        if (!isMounted) return;

        if (!moduleData) {
          setError("Module not found");
          setLoading(false);
          return;
        }
        setModule(moduleData);
        setCompletedCourseName(null); // Reset course completion indicator when changing modules

        // Get course data for breadcrumbs
        const courseData = await findCourseByModuleId(moduleId, currentUser?.uid);
        if (isMounted) {
          setCourse(courseData);
        }

        // Get steps data
        // const stepsData = await getModuleSteps(moduleId);
        const stepsData = moduleData.steps || [];
        if (!isMounted) return;
        // console.log("module Data");
        // console.log(moduleData);
        // console.log("step data");
        // console.log(stepsData);
        if (stepsData.length === 0) {
          setError("No steps found for this module");
          setLoading(false);
          return;
        }

        // Steps are already sorted by order from backend
        setSteps(stepsData);

        // Pre-calculate next module within course to ensure UI buttons (Home/Retake) are correct
        try {
          const nextInCourse = await getNextModuleInCourse(moduleId, currentUser?.uid);
          setNextModule(nextInCourse);
        } catch (err) {
          console.error("Error pre-calculating next module:", err);
        }

        // Set default active step
        setActiveStep(0);
        setModuleCompleted(false);

        // Show content guide for "introduction" module
        if (moduleData.name === "Introduction" && currentUser) {
          // Small delay to ensure UI is rendered
          setTimeout(() => {
            setShowContentGuide(true);
          }, 1000);
        }



        if (isMounted) {
          setLoading(false);
        }
      } catch (err) {
        console.error("Error loading module data:", err);
        if (isMounted) {
          setError("Failed to load module data");
          setLoading(false);
        }
      }
    };

    loadModuleData();

    return () => {
      isMounted = false;
    };
  }, [moduleId]); // Chỉ phụ thuộc vào moduleId để tránh gọi API nhiều lần

  useEffect(() => {
    setQuizLoading(isQuizLoading);
    if (!quizLoading) {
      setHasQuiz(Array.isArray(quizData) ? quizData.length > 0 : !!quizData);
    }
    setQuizLoading(isQuizLoading);
  }, [quizData, quizLoading]);


  // Load user progress separately and set initial activeStep correctly
  useEffect(() => {
    const loadUserProgress = async () => {
      if (!currentUser || !steps.length || initialLoadComplete) {
        return;
      }

      // Nếu đang retake mode: luôn bắt đầu từ step đầu tiên, giữ moduleCompleted = true
      // để load next module và hiện đúng nút (module đã completed rồi, user chỉ xem lại)
      if (isRetakeMode) {
        setActiveStep(0);
        setModuleCompleted(true);
        setInitialLoadComplete(true);
        return;
      }

      try {
        // Lấy tiến trình hiện tại của người dùng cho module này
        const moduleProgress = await getModuleProgress(
          currentUser.uid,
          moduleId
        );

        if (
          moduleProgress &&
          (moduleProgress.stepId || moduleProgress.currentStepId)
        ) {
          // Sử dụng currentStepId hoặc stepId tùy thuộc vào cấu trúc dữ liệu
          let savedStepId =
            moduleProgress.currentStepId || moduleProgress.stepId;

          const moduleProgressVersion = moduleProgress.version ?? 1;

          if (
            moduleProgressVersion < module.version &&
            savedStepId === "completed"
          )
            savedStepId = steps[0];

          // Kiểm tra xem module đã hoàn thành chưa
          if (savedStepId === "completed") {
            // Nếu module đã hoàn thành, luôn bắt đầu từ bước đầu tiên (theo yêu cầu)
            setActiveStep(0);
            setModuleCompleted(true);
          } else {
            // Tìm index của step trong danh sách steps
            const stepIndex = steps.findIndex(
              (step) => step.id === savedStepId
            );
            if (stepIndex !== -1) {
              setActiveStep(stepIndex);
            } else {
              // Nếu không tìm thấy step, bắt đầu từ step đầu tiên
              setActiveStep(0);
            }
            setModuleCompleted(false);
          }
        } else {
          // Nếu chưa có tiến trình, bắt đầu từ step đầu tiên
          setActiveStep(0);
          setModuleCompleted(false);
        }

        // Đánh dấu đã hoàn thành việc load initial data
        setInitialLoadComplete(true);
      } catch (err) {
        console.error("Error getting module progress:", err);
        setActiveStep(0);
        setModuleCompleted(false);
        setInitialLoadComplete(true);
      }
    };

    loadUserProgress();
  }, [currentUser, moduleId, steps, initialLoadComplete]);

  // Cache để tránh gọi API nhiều lần cho cùng một step
  const progressCacheRef = useRef({});
  // Ref to track the latest step index the user has navigated to
  const latestStepRef = useRef(null);

  // Update user progress - uses latest-step-ref pattern to handle rapid navigation
  const updateProgress = useCallback(async () => {
    // Chỉ update progress sau khi đã load xong initial data
    if (
      !currentUser ||
      !module ||
      steps.length === 0 ||
      !initialLoadComplete ||
      isRetakeMode // Don't update progress if in retake mode
    ) {
      return;
    }

    // Prevent concurrent updates - will retry via latestStepRef after current finishes
    if (isUpdatingProgressRef.current) {
      return;
    }

    // Get the latest step the user navigated to
    const stepIndex = latestStepRef.current;
    if (stepIndex === null || stepIndex >= steps.length) {
      return;
    }

    const currentStepData = steps[stepIndex];
    const progressKey = `${moduleId}_${currentStepData.id}`;

    // Kiểm tra cache trước khi gọi API
    if (progressCacheRef.current[progressKey]) {
      return;
    }

    // Only update when step changes
    if (prevStepRef.current !== progressKey) {
      prevStepRef.current = progressKey;
      isUpdatingProgressRef.current = true;

      try {
        let moduleProgress = null;
        let savedStepId = null;
        try {
          moduleProgress = await getModuleProgress(
            currentUser.uid,
            moduleId
          );
          savedStepId =
            moduleProgress?.currentStepId || moduleProgress?.stepId;
          const moduleProgressVersion = moduleProgress?.version ?? 1;
          const moduleVersion = module?.version ?? 1;
          if (
            moduleProgressVersion < moduleVersion &&
            savedStepId === "completed"
          ) {
            savedStepId = steps[0];
          }
        } catch (_e) {
          savedStepId = null;
        }
        // Nếu module đã hoàn thành, không cập nhật lại
        if (savedStepId === "completed") {
          setModuleCompleted(true);
          progressCacheRef.current[progressKey] = true;
          message.info("Module already completed");
          return;
        }

        // Chỉ update nếu step hiện tại khác với step đã lưu hoặc chưa có progress
        if (!savedStepId || savedStepId !== currentStepData.id) {
          await updateModuleProgress(
            currentUser.uid,
            moduleId,
            currentStepData.id,
            false, // not completed yet
            null, // no quiz score yet
            module?.version ?? 1
          );

          // Cache kết quả thành công
          progressCacheRef.current[progressKey] = true;

          message.info(`Progress updated to ${currentStepData.title}`);
        } else {
          // Nếu step đã được lưu rồi, chỉ cache kết quả
          progressCacheRef.current[progressKey] = true;
        }
      } catch (err) {
        console.error("Error updating progress:", err);
        message.error("Error updating progress");
      } finally {
        isUpdatingProgressRef.current = false;

        // After API completes, check if user navigated to a newer step while we were saving
        // If so, retry to save the latest step
        const latestStep = latestStepRef.current;
        if (latestStep !== null && latestStep !== stepIndex && latestStep < steps.length) {
          const latestKey = `${moduleId}_${steps[latestStep].id}`;
          if (!progressCacheRef.current[latestKey]) {
            // Schedule retry for the latest step
            updateProgress();
          }
        }
      }
    }
  }, [activeStep, currentUser, moduleId, module, steps, initialLoadComplete, message]);

  useEffect(() => {
    // Chỉ cập nhật khi có đủ dữ liệu
    if (!currentUser || !module || !steps.length) {
      return;
    }

    // Lấy thông tin về bước hiện tại
    const currentStepData = steps[activeStep];
    if (!currentStepData) {
      return;
    }

    if (currentStepData.videoUrl || currentStepData.imageUrl) {
      setMainModelLoaded(false);
    }

    // Always update the latest step ref to track what user navigated to
    latestStepRef.current = activeStep;

    // Tạo key duy nhất cho mỗi bước
    const stepKey = `${moduleId}_${currentStepData.id}`;

    // Kiểm tra cache trước khi gọi API
    if (progressCacheRef.current[stepKey]) {
      return;
    }
    // Short debounce - but latestStepRef ensures final step is always saved
    const timer = setTimeout(() => {
      updateProgress();
    }, 300);

    return () => {
      clearTimeout(timer);
    };
  }, [activeStep, currentUser, moduleId, module, steps, updateProgress]);

  // Handler for completing module when no quiz
  const handleCompleteModule = async () => {
    if (isActionLoading) return;
    try {
      setIsActionLoading(true);
      // Mark module as completed
      await updateModuleProgress(
        currentUser.uid,
        moduleId,
        "completed", // Special stepId to indicate completion
        true, // Module is completed
        null, // No quiz score
        module?.version ?? 1
      );

      // Invalidate React Query cache so progress is fresh on navigation
      refreshProgress();

      setModuleCompleted(true);
      message.success("Module completed successfully!");

      // Kiểm soát logic hoàn thành course
      try {
        const { isLast, course } = await isLastModuleInCourse(
          moduleId,
          (id) => id === moduleId || checkModuleCompleted(id),
          currentUser?.uid
        );

        if (isLast && course) {
          // Lưu tên khóa học để hiển thị UI đặc biệt
          setCompletedCourseName(course.name);

          // Gọi API complete-course
          try {
            await completeCourse(currentUser.uid, course.id);
          } catch (err) {
            console.error("Error calling completeCourse API:", err);
          }

          // Hiển thị pháo hoa
          setTimeout(() => {
            setShowFireworks(true);
          }, 1000);
        } else {
          // Nếu chưa xong course, tìm module tiếp theo TRONG KHÓA HỌC để hiện nút "Next Module"
          const nextModuleData = await getNextModuleInCourse(
            moduleId,
            currentUser?.uid
          );
          setNextModule(nextModuleData);
        }
      } catch (error) {
        console.error("Error checking course completion:", error);
      }
    } catch (error) {
      console.error("Error completing module:", error);
      message.error("Error completing module");
    } finally {
      setIsActionLoading(false);
    }
  };

  // Handler: Chuyển sang module tiếp theo
  const handleNextModule = () => {
    if (isActionLoading) return;
    if (nextModule) {
      // Nếu đang retake mode, tiếp tục truyền param retake=true
      const suffix = isRetakeMode ? "?retake=true" : "";
      navigate(`/module/${nextModule.id}${suffix}`);
    }
  };

  // Handler: Học lại khóa học từ đầu (không reset progress)
  const handleRetakeCourse = async () => {
    try {
      const course = await findCourseByModuleId(moduleId, currentUser?.uid);
      if (course && course.modules && course.modules.length > 0) {
        navigate(`/module/${course.modules[0].id}?retake=true`);
      } else {
        navigate("/");
      }
    } catch (error) {
      console.error("Error retaking course:", error);
      navigate("/");
    }
  };

  useEffect(() => {
    const loadNextModuleInfo = async () => {
      if (moduleCompleted) {
        try {
          // Luôn lấy module tiếp theo trong cùng khóa học, không nhảy sang khóa học khác
          const nextModuleData = await getNextModuleInCourse(
            moduleId,
            currentUser?.uid
          );
          setNextModule(nextModuleData);
        } catch (error) {
          console.error("Error loading next module info:", error);
        }
      }
    };

    if (initialLoadComplete) {
      loadNextModuleInfo();
    }
  }, [
    moduleCompleted,
    initialLoadComplete,
    moduleId,
    currentUser,
    isRetakeMode,
    checkModuleCompleted,
  ]);

  // Navigate to a specific step (for accordion click on completed steps)
  const handleStepChange = (stepIndex) => {
    if (stepIndex >= 0 && stepIndex < steps.length) {
      const stepKey = `${moduleId}_${steps[stepIndex].id}`;
      delete progressCacheRef.current[stepKey];
      setActiveStep(stepIndex);
    }
  };

  // Navigation handlers - FIXED LOGIC
  const handleNext = () => {
    // Đơn giản hóa logic: chỉ chuyển đến step tiếp theo
    // Việc update progress sẽ được xử lý bởi useEffect khác
    setActiveStep((prevActiveStep) => {
      const nextStep = prevActiveStep + 1;
      if (nextStep < steps.length) {
        // Clear cache cho step tiếp theo để đảm bảo nó được cập nhật
        const nextStepKey = `${moduleId}_${steps[nextStep].id}`;
        delete progressCacheRef.current[nextStepKey];
        return nextStep;
      }
      return prevActiveStep; // Không thay đổi nếu đã ở step cuối
    });
  };

  const handleBack = () => {
    // Đơn giản hóa logic: chỉ chuyển đến step trước đó
    setActiveStep((prevActiveStep) => {
      const previousStep = prevActiveStep - 1;
      if (previousStep >= 0) {
        // Clear cache cho step trước đó để đảm bảo nó được cập nhật lại
        const previousStepKey = `${moduleId}_${steps[previousStep].id}`;
        delete progressCacheRef.current[previousStepKey];
        return previousStep;
      }
      return prevActiveStep; // Không thay đổi nếu đã ở step đầu
    });
  };

  // Handler for finishing a module in retake mode (especially the last one)
  const handleFinishRetake = async () => {
    try {
      setIsActionLoading(true);
      // Check if it's the last module
      const { isLast, course: courseData } = await isLastModuleInCourse(
        moduleId,
        (id) => id === moduleId || checkModuleCompleted(id),
        currentUser?.uid
      );

      if (isLast && courseData) {
        // Set completed course name to show Home/Retake buttons, but DO NOT set showFireworks
        setCompletedCourseName(courseData.name);
      } else {
        // If not last, just show success and maybe navigate back or stay
        message.success("Module review completed!");
      }
    } catch (error) {
      console.error("Error in handleFinishRetake:", error);
    } finally {
      setIsActionLoading(false);
    }
  };

  // Start interactive tour
  const startIntro = () => {
    const currentStepData = steps[activeStep];

    if (!currentStepData) {
      message.info("No tour available for this step");
      return;
    }
    // Wait for elements to be rendered
    setTimeout(() => {
      let introSteps = [];

      // Only use hotspots for tour - introjsSteps functionality disabled
      if (currentStepData.hotspots && currentStepData.hotspots.length > 0) {
        // Add module viewer container as first step
        const viewerContainer = document.querySelector(
          "#module-viewer-container"
        );
        if (viewerContainer) {
          introSteps.push({
            element: viewerContainer,
            intro:
              "This is the 3D model viewer area. You can rotate, zoom, and pan the model.",
            position: "bottom",
          });
        }

        // Add hotspots to tour
        currentStepData.hotspots.forEach((hotspot) => {
          const element = document.querySelector(`#${hotspot.id}`);
          if (element) {
            introSteps.push({
              element,
              intro:
                hotspot.description ||
                hotspot.intro ||
                `This is ${hotspot.label || hotspot.id}`,
              position: hotspot.introPosition || "bottom",
            });
          } else {
            console.warn(`Hotspot element not found: #${hotspot.id}`);
          }
        });
      }

      // Always add tour button as last step
      const tourButton = document.querySelector("#tour-button");
      if (tourButton) {
        introSteps.push({
          element: tourButton,
          intro: "Click here to restart the tour anytime.",
          position: "left",
        });
      }

      if (introSteps.length === 0) {
        message.info("No tour available for this step");
        return;
      }
      // Start IntroJS with improved options
      introJs()
        .setOptions({
          steps: introSteps,
          nextLabel: "Next",
          prevLabel: "Back",
          skipLabel: "Skip",
          doneLabel: "Done",
          // Improved positioning options
          scrollToElement: true,
          showStepNumbers: false,
          exitOnOverlayClick: false,
          exitOnEsc: true,
          helperElementPadding: 10,
          positionPrecedence: ["bottom", "top", "right", "left"],
          // Add callbacks for debugging
          onbeforechange: function (targetElement) {
            if (targetElement) {
              // Ensure element is visible
              targetElement.scrollIntoView({
                behavior: "smooth",
                block: "center",
              });
            }
          },
          onchange: function (targetElement) { },
        })
        .start();
    }, 1000); // Increased timeout to ensure 3D elements are rendered
  };

  // Loading state
  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div style={{ padding: 24 }}>
        <Alert type="error" message={error} />
        <Button
          type="primary"
          onClick={() => navigate("/")}
          style={{ marginTop: 16 }}
        >
          Back to Home
        </Button>
      </div>
    );
  }

  // No content state
  if (!module || steps.length === 0) {
    return (
      <div style={{ padding: 24 }}>
        <Alert type="warning" message="No content available" />
        <Button
          type="primary"
          onClick={() => navigate("/")}
          style={{ marginTop: 16 }}
        >
          Back to Home
        </Button>
      </div>
    );
  }

  const currentStep = steps[activeStep];
  const currentStepCameraPosition = currentStep?.camera?.position || [5, 5, 5];
  const currentStepCameraFov = currentStep?.camera?.fov || 50;

  // ── HELPER: GET STEP ICON ──
  const getStepIcon = (step, isActive, isDone) => {
    let icon = <Icon as={FileText} />;
    if (step.videoUrl) icon = <Icon as={CirclePlay} strokeWidth={2.5} />;
    else if (step.imageUrl) icon = <Icon as={Book} />;
    else if (step.isQuiz) icon = <Icon as={CircleHelp} />;

    const color = isActive ? antToken.colorPrimary : (isDone ? "var(--color-success)" : antToken.colorTextTertiary);
    return React.cloneElement(icon, { size: 18, style: { color } });
  };

  const getLessonTypeLabel = (step) => {
    if (isCustomPageStep(step)) return "Custom Lesson";
    if (step.videoUrl) return "Video Lesson";
    if (step.imageUrl) return "Image Lesson";
    return "3D Lesson";
  };

  // ── COMPONENT: STEP LIST ITEM ──
  const StepItem = ({ step, index, isActive, isDone, onClick }) => {
    const typeLabel = isCustomPageStep(step)
      ? "custom"
      : step.videoUrl
        ? "video"
        : step.imageUrl
          ? "image"
          : "3d";
    const durationLabel = `Topic ${index + 1}: ${typeLabel} lesson`;

    return (
      <div
        data-step-index={index}
        onClick={() => {
          if (index <= activeStep + 1 || moduleCompleted) {
            onClick(index);
          } else {
            message.warning("Please complete the topics in order.");
          }
        }}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 16,
          padding: "16px 20px",
          cursor: (index <= activeStep + 1 || moduleCompleted) ? "pointer" : "not-allowed",
          backgroundColor: isActive ? alpha(antToken.colorPrimary, 0.05) : "transparent",
          borderLeft: `4px solid ${isActive ? antToken.colorPrimary : "transparent"}`,
          transition: "all 0.2s ease",
          opacity: (index <= activeStep + 1 || moduleCompleted) ? 1 : 0.5,
        }}
      >
        <div style={{ flexShrink: 0 }}>
          {getStepIcon(step, isActive, isDone)}
        </div>
        <div style={{ flex: 1, overflow: "hidden" }}>
          <Typography.Text
            strong={isActive}
            style={{
              display: "block",
              fontSize: 14,
              color: isActive ? antToken.colorPrimary : antToken.colorText,
              lineHeight: 1.2,
              marginBottom: 4,
              fontWeight: isActive ? 500 : 400,
            }}
          >
            {index + 1}. {step.title}
          </Typography.Text>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {durationLabel}
          </Typography.Text>
        </div>
        {isDone && <Icon as={CircleCheck} strokeWidth={2.5} size={14} style={{ color: "var(--color-success)" }} />}
      </div>
    );
  };

  // ── COMPONENT: LESSON DETAILS ──
  const LessonDetails = ({ step, index }) => {
    const typeLabel = getLessonTypeLabel(step);

    return (
      <div style={{ marginTop: 32, animation: "fadeIn 0.5s ease" }}>
        {/* Type Badge & Meta */}
        <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 16, flexWrap: "wrap" }}>
          <div style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            backgroundColor: alpha(antToken.colorPrimary, 0.1),
            color: antToken.colorPrimary,
            padding: "4px 12px",
            borderRadius: antToken.borderRadiusLG,
            fontSize: 12,
            fontWeight: 500,
          }}>
            <span>{typeLabel}</span>
          </div>
        </div>

        {/* Title */}
        <Typography.Title level={2} style={{
          fontSize: isMobile ? 18 : 24,
          marginBottom: 24,
          textTransform: "none",
          letterSpacing: "normal",
          fontWeight: 500,
          fontFamily: "var(--font-untitled-sans)",
          wordBreak: "break-word",
          overflowWrap: "break-word",
        }}>
          {index + 1}. {step.title}
        </Typography.Title>

        {/* Description / content. After merging Dung's quilljs into dev,
            BOTH legacy steps and custom-page steps may carry RichContent.
            Render the viewer whenever content is non-empty; for custom-page
            also wire up inline media resolution. Empty falls back to the
            generic prompt text. */}
        {isRichContentEmpty(step.content) ? (
          <div style={{ fontSize: 16, lineHeight: 1.8, color: antToken.colorTextSecondary, marginBottom: 0 }}>
            In this comprehensive session, we explore advanced techniques for analyzing emerging markets. We will cover critical macro-economic indicators, competitive landscape mapping, and how to leverage real-time data for strategic positioning.
          </div>
        ) : (
          <RichContentViewer
            content={step.content}
            compact={isMobile}
            style={{ marginBottom: 0 }}
            {...(isCustomPageStep(step)
              ? {
                  mediaResolver: (mediaId) =>
                    (step.media || []).find((m) => m.id === mediaId) || null,
                  onOpenFullscreen3D: (mediaId) => {
                    // Placeholder route — a dedicated FE fullscreen viewer can
                    // be added later. For now no-op (inline preview is
                    // already interactive).
                    // eslint-disable-next-line no-console
                    console.log("Open fullscreen 3D:", mediaId);
                  },
                }
              : {})}
          />
        )}
      </div>
    );
  };

  // ── COMPONENT: NAVIGATION BUTTONS ──
  const NavigationButtons = () => {
    const isFirst = activeStep === 0;
    const isLast = activeStep === steps.length - 1;

    return (
      <div style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginTop: 24,
        paddingTop: 0,
        gap: isMobile ? 8 : 16,
        flexWrap: isMobile ? "wrap" : "nowrap",
      }}>
        {/* Previous Button */}
        <Button
          icon={<Icon as={ArrowLeft} />}
          disabled={isFirst || isActionLoading}
          onClick={handleBack}
          size="large"
          style={{
            height: isMobile ? 44 : 48,
            padding: isMobile ? "0 12px" : "0 24px",
            borderRadius: antToken.borderRadius,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontWeight: 500,
            flex: isMobile ? "0 0 100%" : "none", // Force 100% width on mobile to trigger wrap
            minWidth: isMobile ? "100%" : 140,
            fontSize: isMobile ? 12 : 16,
            marginBottom: isMobile ? 4 : 0, // Add small gap below if wrapped
          }}
        >
          Previous Topic
        </Button>

        <div style={{ 
          display: "flex", 
          gap: isMobile ? 8 : 16, 
          flex: isMobile ? "0 0 100%" : "none", // Force 100% width on mobile
          width: isMobile ? "100%" : "auto",
          justifyContent: isMobile ? "space-between" : "flex-end",
          flexWrap: "nowrap"
        }}>
          {isLast ? (
            moduleCompleted ? (
              (completedCourseName || !nextModule) ? (
                /* Case: Course Completed OR Last Module in Course reached */
                <>
                  <Button
                    type="primary"
                    onClick={() => navigate("/")}
                    icon={<Icon as={Home} />}
                    size="large"
                    style={{
                      height: isMobile ? 44 : 48,
                      borderRadius: antToken.borderRadius,
                      flex: 1, // Take equal space on mobile
                      minWidth: isMobile ? 0 : 120,
                      fontSize: isMobile ? 12 : 16,
                    }}
                  >
                    Home
                  </Button>
                  <Button
                    onClick={handleRetakeCourse}
                    icon={<Icon as={RotateCcw} />}
                    size="large"
                    style={{
                      height: isMobile ? 44 : 48,
                      borderRadius: antToken.borderRadius,
                      flex: 1, // Take equal space on mobile
                      minWidth: isMobile ? 0 : 120,
                      fontSize: isMobile ? 12 : 16,
                    }}
                  >
                    Retake
                  </Button>
                </>
              ) : (
                /* Case: Module Completed but more modules exist in course */
                <>
                  <Button
                    type="primary"
                    onClick={handleNextModule}
                    icon={<Icon as={ArrowRight} />}
                    iconPosition="end"
                    size="large"
                    disabled={isActionLoading}
                    loading={isActionLoading}
                    style={{
                      height: isMobile ? 44 : 48,
                      padding: isMobile ? "0 12px" : "0 24px",
                      borderRadius: antToken.borderRadius,
                      fontWeight: 500,
                      flex: 1,
                      minWidth: isMobile ? 0 : 160,
                      fontSize: isMobile ? 12 : 16,
                    }}
                  >
                    {isActionLoading ? "Navigating..." : "Next Module"}
                  </Button>
                </>
              )
            ) : isRetakeMode ? (
              /* Case: Retake Mode completed (should already be covered by moduleCompleted above) */
              <>
                {nextModule ? (
                  <Button
                    type="primary"
                    onClick={handleNextModule}
                    icon={<Icon as={ArrowRight} />}
                    iconPosition="end"
                    size="large"
                    disabled={isActionLoading}
                    loading={isActionLoading}
                    style={{
                      height: isMobile ? 44 : 48,
                      padding: isMobile ? "0 12px" : "0 24px",
                      borderRadius: antToken.borderRadius,
                      fontWeight: 500,
                      flex: 1,
                      minWidth: isMobile ? 0 : 160,
                      fontSize: isMobile ? 12 : 16,
                    }}
                  >
                    {isActionLoading ? "Navigating..." : "Next Module"}
                  </Button>
                ) : (
                  <>
                    <Button
                      type="primary"
                      onClick={() => navigate("/")}
                      icon={<Icon as={Home} />}
                      size="large"
                      style={{
                        height: isMobile ? 44 : 48,
                        borderRadius: antToken.borderRadius,
                        flex: 1,
                        minWidth: isMobile ? 0 : 120,
                        fontSize: isMobile ? 12 : 16,
                      }}
                    >
                      Home
                    </Button>
                    <Button
                      onClick={handleRetakeCourse}
                      icon={<Icon as={RotateCcw} />}
                      size="large"
                      style={{
                        height: isMobile ? 44 : 48,
                        borderRadius: antToken.borderRadius,
                        flex: 1,
                        minWidth: isMobile ? 0 : 120,
                        fontSize: isMobile ? 12 : 16,
                      }}
                    >
                      Retake
                    </Button>
                  </>
                )}
              </>
            ) : hasQuiz ? (
              /* Case: Pending Quiz */
              <Button
                type="primary"
                onClick={() => navigate(`/module/${moduleId}/quiz`)}
                icon={<Icon as={ArrowRight} />}
                iconPosition="end"
                size="large"
                disabled={quizLoading || isActionLoading}
                loading={isActionLoading}
                style={{
                  height: isMobile ? 44 : 48,
                  padding: isMobile ? "0 12px" : "0 24px",
                  borderRadius: antToken.borderRadius,
                  fontWeight: 500,
                  flex: 1,
                  minWidth: isMobile ? 0 : 180,
                  fontSize: isMobile ? 12 : 16,
                }}
              >
                {quizLoading ? "Checking..." : (isActionLoading ? "Processing..." : "Take Quiz")}
              </Button>
            ) : (
              /* Case: Complete Module Action */
              <Button
                type="primary"
                onClick={handleCompleteModule}
                icon={<Icon as={CircleCheck} />}
                iconPosition="end"
                size="large"
                disabled={quizLoading || isActionLoading}
                loading={isActionLoading}
                style={{
                  height: isMobile ? 44 : 48,
                  padding: isMobile ? "0 12px" : "0 24px",
                  borderRadius: antToken.borderRadius,
                  fontWeight: 500,
                  flex: 1,
                  minWidth: isMobile ? 0 : 180,
                  fontSize: isMobile ? 12 : 16,
                }}
              >
                {quizLoading ? "Checking..." : (isActionLoading ? "Completing..." : "Complete Module")}
              </Button>
            )
          ) : (
            /* Case: Middle of module - Continue */
            <Button
              type="primary"
              onClick={handleNext}
              icon={<Icon as={ArrowRight} />}
              iconPosition="end"
              size="large"
              loading={isActionLoading}
              style={{
                height: isMobile ? 44 : 48,
                padding: isMobile ? "0 12px" : "0 24px",
                borderRadius: antToken.borderRadius,
                fontWeight: 500,
                flex: 1,
                minWidth: isMobile ? 0 : 160,
                fontSize: isMobile ? 12 : 16,
              }}
            >
              Next Topic
            </Button>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <MainLayout>
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "80vh" }}>
          <Spin size="large" />
        </div>
      </MainLayout>
    );
  }

  if (error) {
    return (
      <MainLayout>
        <div style={{ padding: 48, textAlign: "center" }}>
          <Alert type="error" message={error} showIcon />
          <Button icon={<Icon as={Home} />} onClick={() => navigate("/")} style={{ marginTop: 24 }}>
            Back to Home
          </Button>
        </div>
      </MainLayout>
    );
  }

  const currentModuleProgressText = `${activeStep + 1} of ${steps.length} topics completed`;

  return (
    <MainLayout>
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .viewer-card {
          box-shadow: 0 4px 20px rgba(0,0,0,0.08) !important;
          border-radius: ${antToken.borderRadiusLG}px !important;
          overflow: hidden !important;
          border: 1px solid ${antToken.colorBorderSecondary} !important;
        }
        .lesson-sidebar {
          background: transparent;
          border-right: none;
        }
        @media (max-width: 768px) {
          .mobile-banner {
            height: 200px;
            width: 100%;
            object-fit: cover;
            border-radius: 0;
            margin-bottom: 24px;
          }
        }
      `}</style>

      {isMobile ? (
        /* ── MOBILE LAYOUT ── */
        <div style={{ padding: "12px 16px", maxWidth: 1400, margin: "0 auto", minHeight: "100vh" }}>
          <div style={{ marginBottom: 16, fontSize: 13, color: antToken.colorTextTertiary }}>
            {course?.name} {">"} {module?.name}
          </div>

          <div style={{ marginBottom: 16 }}>
            <Typography.Title
              level={4}
              style={{ margin: "0 0 4px 0", fontSize: 20, fontWeight: 500 }}
            >
              {module.name}
            </Typography.Title>
            <Typography.Text type="secondary" style={{ fontSize: 13 }}>
              {currentModuleProgressText}
            </Typography.Text>
          </div>


            {/* Viewer Container — skipped for custom-page steps which
                render all media inline via RichContentViewer below. */}
            {!isCustomPageStep(currentStep) && (
            <div
              style={{
                aspectRatio: "16/9",
                width: "100%",
                position: "relative",
                borderRadius: antToken.borderRadiusLG,
                overflow: "hidden",
                backgroundColor: antToken.colorBgContainer,
                border: `1px solid ${antToken.colorBorderSecondary}`,
                marginBottom: 24,
              }}
            >
              {currentStep?.videoUrl || currentStep?.imageUrl ? (
                <MediaViewer step={currentStep} />
              ) : (
                <>
                  {/* Reset Camera Button */}
                  {mainModelLoaded && (
                    <div
                      style={{
                        position: "absolute",
                        bottom: 20,
                        right: 20,
                        zIndex: 100,
                      }}
                    >
                      <Tooltip title="Reset Camera" placement="left">
                        <Button
                          shape="circle"
                          icon={<Icon as={RotateCcw} />}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (controlsRef.current) {
                              resetCameraToDefault(
                                controlsRef.current.object,
                                controlsRef,
                                currentStep
                              );
                            }
                          }}
                          style={{
                            width: 44,
                            height: 44,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: "rgba(255, 255, 255, 0.9)",
                            border: "none",
                            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                            color: antToken.colorPrimary,
                          }}
                        />
                      </Tooltip>
                    </div>
                  )}

                  <Canvas
                    shadows
                    camera={{ position: currentStepCameraPosition, fov: currentStepCameraFov }}
                    style={{ height: "100%", width: "100%" }}
                    gl={{ antialias: true, alpha: true }}
                  >
                    <ambientLight intensity={1} />
                    <directionalLight
                      position={[10, 10, 5]}
                      intensity={1}
                      castShadow
                    />
                    <Suspense fallback={null}>
                      <ModelWithMemo
                        key={module.modelUrl}
                        modelUrl={module.modelUrl}
                        onLoadStart={handleMainModelLoadStart}
                        onLoadComplete={handleMainModelLoadComplete}
                        layers={currentStep?.layers}
                      />
                    </Suspense>
                    {mainModelLoaded && (
                      <>
                        <OrbitControls
                          ref={controlsRef}
                          makeDefault
                          target={currentStep?.camera?.target || [0, 0, 0]}
                          enableDamping={true}
                          dampingFactor={0.05}
                          rotateSpeed={1.2}
                          zoomSpeed={1.2}
                          panSpeed={1.0}
                          autoRotate={false}
                          minDistance={0.5}
                          maxDistance={50}
                          minPolarAngle={0}
                          maxPolarAngle={Math.PI}
                          enableKeys={true}
                          keyPanSpeed={7.0}
                          screenSpacePanning={false}
                          touches={orbitControlsSettings.touches}
                          mouseButtons={orbitControlsSettings.mouseButtons}
                        />
                        <CameraController
                          cameraSettings={currentStep?.camera}
                          controlsRef={controlsRef}
                        />
                        <HotspotsRenderer
                          hotspots={currentStep?.hotspots || []}
                          onHotspotClick={handleHotspotClick}
                        />
                      </>
                    )}
                  </Canvas>
                </>
              )}
            </div>
            )}

            {/* Details */}
            <LessonDetails step={currentStep} index={activeStep} />

            {/* Mobile Topics List */}
            <div style={{ marginTop: 32 }}>
              <Typography.Title level={4} style={{ marginBottom: 16, fontWeight: 500 }}>
                Topics
              </Typography.Title>
              <div style={{
                border: `1px solid ${antToken.colorBorderSecondary}`,
                borderRadius: antToken.borderRadiusLG,
                overflow: "hidden",
                backgroundColor: antToken.colorBgContainer,
              }}>
                {steps.map((step, idx) => (
                  <React.Fragment key={step.id}>
                    <StepItem
                      step={step}
                      index={idx}
                      isActive={idx === activeStep}
                      isDone={idx < activeStep || (idx === activeStep && moduleCompleted)}
                      onClick={handleStepChange}
                    />
                    {idx < steps.length - 1 && (
                      <div style={{ height: 1, backgroundColor: antToken.colorBorderSecondary, margin: "0 20px" }} />
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>

            {/* Nav */}
            <NavigationButtons />
        </div>
      ) : (
        /* ── DESKTOP LAYOUT ── */
        <div style={{ maxWidth: 1400, margin: "0 auto", padding: "16px 24px", display: "flex", gap: 64 }}>
          
          {/* Sidebar */}
          <div style={{ width: 320, flexShrink: 0 }}>
            <Typography.Title level={4} style={{ 
              fontSize: 22, 
              marginBottom: 8, 
              textTransform: "none", 
              letterSpacing: "normal",
              fontWeight: 500,
              fontFamily: "var(--font-untitled-sans)"
            }}>
              {module.name}
            </Typography.Title>
            <Typography.Text type="secondary" style={{ display: "block", marginBottom: 32, fontSize: 14 }}>
              {currentModuleProgressText}
            </Typography.Text>

            <div style={{ 
              border: `1px solid ${antToken.colorBorderSecondary}`, 
              borderRadius: antToken.borderRadiusLG, 
              overflow: "hidden",
              backgroundColor: antToken.colorBgContainer
            }}>
              {steps.map((step, idx) => (
                <React.Fragment key={step.id}>
                  <StepItem
                    step={step}
                    index={idx}
                    isActive={idx === activeStep}
                    isDone={idx < activeStep || (idx === activeStep && moduleCompleted)}
                    onClick={handleStepChange}
                  />
                  {idx < steps.length - 1 && <div style={{ height: 1, backgroundColor: antToken.colorBorderSecondary, margin: "0 20px" }} />}
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* Main Content Area */}
          <div style={{ flex: 1, minWidth: 0 }}>
            {/* Breadcrumbs */}
            <div style={{ marginBottom: 16, fontSize: 13, color: antToken.colorTextTertiary }}>
              {course?.name} {">"} {module?.name}
            </div>

            {/* Viewer Container — skipped for custom-page steps which
                render all media inline via RichContentViewer below. */}
            {!isCustomPageStep(currentStep) && (
            <div style={{
              height: isMobile ? "auto" : "60vh",
              aspectRatio: isMobile ? "16/9" : "unset",
              width: "100%",
              minHeight: isMobile ? "unset" : 450, 
              position: "relative", 
              borderRadius: antToken.borderRadiusLG, 
              overflow: "hidden",
              backgroundColor: antToken.colorBgContainer,
              border: `1px solid ${antToken.colorBorderSecondary}`,
            }}>
              {currentStep?.videoUrl || currentStep?.imageUrl ? (
                <MediaViewer step={currentStep} />
              ) : (
                <>
                  {/* Reset Camera Button - OUTSIDE Canvas */}
                  {mainModelLoaded && (
                    <div style={{ position: "absolute", bottom: 20, right: 20, zIndex: 100 }}>
                      <Tooltip title="Reset Camera" placement="left">
                        <Button
                          shape="circle"
                          icon={<Icon as={RotateCcw} />}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (controlsRef.current) {
                              resetCameraToDefault(controlsRef.current.object, controlsRef, currentStep);
                            }
                          }}
                          style={{
                            width: 44,
                            height: 44,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: "rgba(255, 255, 255, 0.9)",
                            border: "none",
                            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                            color: antToken.colorPrimary,
                          }}
                        />
                      </Tooltip>
                    </div>
                  )}

                  <Canvas
                    shadows
                    camera={{ position: currentStepCameraPosition, fov: currentStepCameraFov }}
                    style={{ height: "100%", width: "100%" }}
                    gl={{ antialias: true, alpha: true }}
                  >
                    <ambientLight intensity={1} />
                    <directionalLight position={[10, 10, 5]} intensity={1} castShadow />
                    <Suspense fallback={null}>
                      <ModelWithMemo
                        key={module.modelUrl}
                        modelUrl={module.modelUrl}
                        onLoadStart={handleMainModelLoadStart}
                        onLoadComplete={handleMainModelLoadComplete}
                        layers={currentStep?.layers}
                      />
                    </Suspense>
                    {mainModelLoaded && (
                      <>
                        <OrbitControls
                          ref={controlsRef}
                          makeDefault
                          target={currentStep?.camera?.target || [0, 0, 0]}
                          enableDamping={true}
                          dampingFactor={0.05}
                          rotateSpeed={1.2}
                          zoomSpeed={1.2}
                          panSpeed={1.0}
                          autoRotate={false}
                          minDistance={0.5}
                          maxDistance={50}
                          minPolarAngle={0}
                          maxPolarAngle={Math.PI}
                          enableKeys={true}
                          keyPanSpeed={7.0}
                          screenSpacePanning={false}
                          touches={orbitControlsSettings.touches}
                          mouseButtons={orbitControlsSettings.mouseButtons}
                        />
                        <CameraController cameraSettings={currentStep?.camera} controlsRef={controlsRef} />
                        <HotspotsRenderer hotspots={currentStep?.hotspots || []} onHotspotClick={handleHotspotClick} />
                      </>
                    )}
                  </Canvas>
                </>
              )}
            </div>
            )}

            {/* Lesson Info */}
            <LessonDetails step={currentStep} index={activeStep} />


            {/* Nav */}
            <NavigationButtons />
          </div>
        </div>
      )}

      {/* Overlays */}
      <ContentGuide open={showContentGuide} onClose={() => setShowContentGuide(false)} moduleId={moduleId} />
      <Fireworks show={showFireworks} onComplete={handleFireworksComplete} courseName={completedCourseName} />
    </MainLayout>
  );
}

export default ModuleViewer;
